import { useMemo } from "react";
import { useAuth } from "../contexts/AuthProvider";
import { useHospitalData } from "../contexts/HospitalDataProvider";
import { useNowEveryMinute } from "../hooks/useNowEveryMinute";
import type { DemoOriginsContext } from "./demoRequestOriginSeed";
import { isNurseSchedulableInDepartment } from "./formatters";

/**
 * What the demo ledgers are seeded from, read off the loaded department. `null` when `enabled` is false, so a real
 * department never builds the roster. Only `inhouse\d*__` demo departments enable it.
 */
export const useDemoOriginsContext = (enabled: boolean): DemoOriginsContext | null => {
	const { hospitalData, chosenDepartment, timezone } = useHospitalData();
	const { userInfo } = useAuth();
	const now = useNowEveryMinute();
	return useMemo<DemoOriginsContext | null>(() => {
		if (!enabled) return null;
		const departmentId = chosenDepartment.department_id;
		return {
			roster: hospitalData.nurses.filter((nurse) => isNurseSchedulableInDepartment(nurse, departmentId)).sort((a, b) => a.id - b.id),
			// A user account has one name field, as the API's off-roster people do.
			currentUser: userInfo.id === undefined ? null : { userId: userInfo.id, person: { nurse_id: null, first_name: userInfo.first_name, last_name: null } },
			ratioLocations: chosenDepartment.locations
				.filter((location) => location.ratioLadder !== undefined && location.ratioLadder.length > 0)
				.map((location) => location.location),
			timezone,
			dayStart: chosenDepartment.dayStart,
			graceMinutes: chosenDepartment.scheduled_shift_delta_minutes,
			now: now.setZone(timezone),
		};
	}, [enabled, hospitalData.nurses, chosenDepartment, timezone, userInfo.id, userInfo.first_name, now]);
};
